import React, { type ReactNode } from 'react';

import type { ResultEventAttributes } from '../../common/utils/events/advanced-search-result-interacted';
import { useFireContextMenuButtonClicked } from '../events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../events/use-fire-summarize-in-chat-button-clicked';
import { useCommonAndPrivateAttributes } from './index';
import {
	getResultAttributesAtRender,
	type PreparedResultAttributes,
	type SharedEventAttributes,
} from './prepared-result-attributes';
import type { ResultAnalyticsCallbacks } from './result-analytics-callbacks';
import { SharedResultAnalytics } from './shared-result-analytics';

type Props = ResultEventAttributes & {
	preparedResultAttributes?: PreparedResultAttributes;
	children: (callbacks: ResultAnalyticsCallbacks) => ReactNode;
};

/** Separate components keep both paths unconditional and retain the nearest AnalyticsContext. */
export const ResultAnalytics = ({
	preparedResultAttributes: prepared,
	...props
}: Props): React.JSX.Element => {
	return prepared ? (
		<SharedResultAnalytics {...props} preparedResultAttributes={prepared} />
	) : (
		<LegacyResultAnalytics {...props} />
	);
};

const LegacyResultAnalytics = ({ children, ...props }: Props): React.JSX.Element => {
	const onResultClicked = useFireResultClicked(props);
	const onResultContextMenu = useFireResultCtxMenuClicked(props);
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata: props.metadata,
		position: props.position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();
	return (
		<>
			{children({
				onResultClicked,
				onResultContextMenu,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			})}
		</>
	);
};

type AttributesProps = {
	preparedResultAttributes?: PreparedResultAttributes;
	children: (attributes: SharedEventAttributes) => ReactNode;
};

export const ResultCommonAttributes = ({
	preparedResultAttributes: prepared,
	...props
}: AttributesProps): React.JSX.Element => {
	return prepared ? (
		<>{props.children(getResultAttributesAtRender(prepared))}</>
	) : (
		<LegacyResultCommonAttributes {...props} />
	);
};

const LegacyResultCommonAttributes = ({ children }: AttributesProps): React.JSX.Element => {
	const attributes = useCommonAndPrivateAttributes();
	return <>{children(attributes)}</>;
};
